import React, { MouseEvent, useState } from 'react'
import { Link } from 'react-router-dom'

import { A, Div } from '@driftt/tide-core'

import usePathUtils from 'hooks/usePathUtils'
import { Insight } from 'modules/accountActivity/domain'
import { ActivityGroupByEndUser, CompanyData } from 'modules/accountActivity/domain'
import { accountPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import ContactAndActions from '../ContactAndActions'

import ActivityList from './ActivityList'

import styles from './styles.module.css'

interface ActivityContentProps {
  insight: Insight
  maxContactToShow: number
  activityByEndUser: ActivityGroupByEndUser[]
  companyData: CompanyData
  presentEndUserIds?: Set<number>
}

interface ContactWithActivitiesProps {
  insight: Insight
  contact: ActivityGroupByEndUser
  presentEndUserIds?: Set<number>
  companyData: CompanyData
}

const ContactWithActivities: React.FC<ContactWithActivitiesProps> = ({
  contact,
  insight,
  presentEndUserIds,
  companyData
}) => {
  const [numberActivities, setNumberActivities] = useState(5)
  return (
    <Div className={styles.contactAndActivities}>
      <ContactAndActions
        insight={insight}
        isOnline={!!presentEndUserIds?.has(contact.endUserId)}
        contact={contact}
        companyData={companyData}
      />
      <ActivityList
        activityList={contact.activityList}
        maxActivitiesToShow={numberActivities}
        onClickShowMoreActivities={() => setNumberActivities(contact.activityList.length)}
      />
    </Div>
  )
}

const ActivityContent: React.FC<ActivityContentProps> = ({
  insight,
  maxContactToShow,
  activityByEndUser,
  companyData,
  presentEndUserIds
}) => {
  const [numContactsToShow, setNumContactsToShow] = useState(maxContactToShow)
  const { isAccountDetailsPage } = usePathUtils()

  const handleLoadMore = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    segmentTrack(TrackType.INSIGHT_FEED_SHOW_MORE_CONTACTS, {
      insightId: insight.id,
      accountId: insight.accountId
    })
    setNumContactsToShow(Math.min(numContactsToShow + 6, activityByEndUser.length))
  }

  return (
    <Div className={styles.ActivityContent}>
      {activityByEndUser
        .slice(0, numContactsToShow)
        .map((contact: ActivityGroupByEndUser, contactIndex: number) => (
          <ContactWithActivities
            insight={insight}
            contact={contact}
            key={contactIndex}
            presentEndUserIds={presentEndUserIds}
            companyData={companyData}
          />
        ))}
      <Div className={styles.footer}>
        {activityByEndUser.length > numContactsToShow && (
          <A className={styles.loadMore} onClick={handleLoadMore}>
            Load more session activity
          </A>
        )}
        {!isAccountDetailsPage && (
          <Link
            className={styles.viewActivity}
            to={accountPath(insight.accountId)}
            onClick={() => {
              segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
                insightId: insight.id,
                accountId: insight.accountId
              })
            }}>
            View all activity from {companyData.name || companyData.domain}
          </Link>
        )}
      </Div>
    </Div>
  )
}

export default ActivityContent
